import Box from '@mui/material/Box';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';

import { CertificateFormController } from 'certificate/forms';
import { PageDrawer } from 'common/components/PageDrawer';
import { getUrl } from 'route/utils/getUrl';
import {
    CertificateEditDocument,
    IAppPermissionsCertificate,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import { useRouteMatch } from 'route/hooks/useRouteMatch';

interface IProps {
    open?: boolean;
    onClose?(): void;
}

export const CertificateManageDrawer = ({ open, onClose }: IProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isExternalCertificateCreatePage = !!useRouteMatch(
        'EXTERNAL_CERTIFICATE_CREATE'
    );

    const isEdit = !!id;
    const {
        canCreate = false,
        canUpdate = false,
    }: IAppPermissionsCertificate['management'] = useAppPermissions(
        'certificate',
        'management'
    );

    const { data: certificateData, loading: certificateLoading } = useQuery(
        CertificateEditDocument,
        {
            skip: !isEdit || !canUpdate,
            variables: { id: id! },
            fetchPolicy: 'cache-and-network',
        }
    );

    const handleClose = () => {
        if (!!onClose) return onClose();

        const searchParams = new URLSearchParams(location.search);
        const url = `${getUrl('CERTIFICATES_MANAGEMENT_LIST')}?${searchParams}`;

        navigate(url);
    };

    if ((!isEdit && !canCreate) || (isEdit && !canUpdate)) {
        return <FourOFourPage />;
    }

    const certificate = certificateData?.certificate || undefined;

    return (
        <PageDrawer open={open} onClose={handleClose}>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                {certificateLoading && <Loader />}

                {!certificateLoading && (
                    <CertificateFormController
                        certificate={certificate}
                        isExternalCertificateCreatePage={
                            isExternalCertificateCreatePage
                        }
                        onClose={handleClose}
                    />
                )}
            </Box>
        </PageDrawer>
    );
};
